import { AVATARS } from "@playpal/core";
import { useState } from "react";
import { ApiError } from "../lib/api";
import { type Identity, randomName } from "../lib/identity";
import type { Session } from "../lib/session";
import { play } from "../lib/sound";
import { Button, Card, Panel } from "./ui";

interface Props {
	session: Session;
	identity: Identity;
	onDone: () => void;
	onCancel: () => void;
}

type Mode = "create" | "signin";

/**
 * Signing up is: pick a name, pick an animal, choose four numbers. No email, no
 * password, nothing a child has to ask a grown-up for.
 */
export function AuthScreen({ session, identity, onDone, onCancel }: Props) {
	const [mode, setMode] = useState<Mode>("create");
	const [name, setName] = useState(identity.name);
	const [avatar, setAvatar] = useState(identity.avatar);
	const [pin, setPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [createdCode, setCreatedCode] = useState<string | null>(null);

	const submit = async () => {
		setError(null);
		setBusy(true);
		try {
			if (mode === "create") {
				if (pin.length !== 4) throw new ApiError("bad-pin", 400, "A PIN is four numbers.");
				if (pin !== confirmPin) {
					throw new ApiError("pin-mismatch", 400, "The two PINs don't match. Try again.");
				}
				const profile = await session.createProfile({ name, avatar, pin });
				play("win");
				setCreatedCode(profile.code);
			} else {
				await session.signIn({ code, pin });
				play("good");
				onDone();
			}
		} catch (caught) {
			play("bad");
			setError(caught instanceof ApiError ? caught.message : "Something went wrong.");
		} finally {
			setBusy(false);
		}
	};

	if (createdCode) {
		return (
			<Panel>
				<Card className="text-center">
					<h1 className="text-4xl font-black">You're all set! 🎉</h1>
					<p className="mt-3 text-lg">This is your player code. Keep it somewhere safe.</p>
					<p className="my-6 rounded-2xl bg-white/70 py-5 text-4xl font-black tracking-widest dark:bg-black/20">
						{createdCode}
					</p>
					<p className="mb-6 text-indigo-900/80 dark:text-indigo-100/80">
						To play on another device, type this code and your PIN. If you forget the code, ask a
						grown-up to write it down for you — we can't look it up, because we don't know who you
						are.
					</p>
					<Button size="lg" onClick={onDone}>
						Let's play
					</Button>
				</Card>
			</Panel>
		);
	}

	return (
		<Panel>
			<Card>
				<div className="mb-6 flex gap-2">
					{(["create", "signin"] as const).map((option) => (
						<button
							key={option}
							type="button"
							aria-pressed={mode === option}
							onClick={() => {
								setMode(option);
								setError(null);
							}}
							className={`flex-1 rounded-2xl px-4 py-3 text-lg font-bold transition ${
								mode === option
									? "bg-indigo-600 text-white shadow-lg"
									: "bg-white/60 dark:bg-white/10"
							}`}
						>
							{option === "create" ? "New player" : "I have a code"}
						</button>
					))}
				</div>

				{mode === "create" ? (
					<>
						<h1 className="text-3xl font-black">Make a player card</h1>
						<p className="mt-2 mb-5 text-indigo-900/70 dark:text-indigo-100/80">
							It saves your wins so they follow you to any device.
						</p>

						<div className="mb-5 flex flex-wrap items-center gap-3">
							<span className="rounded-2xl bg-white/70 px-4 py-3 text-2xl font-bold dark:bg-white/10">
								{avatar} {name}
							</span>
							<Button tone="secondary" onClick={() => setName(randomName())}>
								🎲 New name
							</Button>
						</div>

						<fieldset className="mb-5">
							<legend className="mb-2 font-semibold">Pick your animal</legend>
							<div className="flex flex-wrap gap-2">
								{AVATARS.map((option) => (
									<button
										key={option}
										type="button"
										aria-label={`Choose ${option}`}
										aria-pressed={option === avatar}
										onClick={() => setAvatar(option)}
										className={`rounded-2xl px-3 py-2 text-3xl transition ${
											option === avatar
												? "scale-110 bg-indigo-600/90 shadow-lg"
												: "bg-white/60 hover:scale-105 dark:bg-white/10"
										}`}
									>
										{option}
									</button>
								))}
							</div>
						</fieldset>

						<PinField label="Choose a 4-number PIN" value={pin} onChange={setPin} />
						<PinField label="Type it again" value={confirmPin} onChange={setConfirmPin} />
					</>
				) : (
					<>
						<h1 className="text-3xl font-black">Welcome back</h1>
						<p className="mt-2 mb-5 text-indigo-900/70 dark:text-indigo-100/80">
							Type the player code you were given, and your PIN.
						</p>
						<label className="mb-4 block">
							<span className="mb-2 block font-semibold">Player code</span>
							<input
								value={code}
								onChange={(event) => setCode(event.target.value.toUpperCase())}
								placeholder="OTTER-1234"
								autoCapitalize="characters"
								autoComplete="off"
								spellCheck={false}
								className="w-full rounded-2xl bg-white/85 px-4 py-3 text-center text-2xl font-black tracking-widest uppercase shadow-inner dark:bg-black/25"
							/>
						</label>
						<PinField label="PIN" value={pin} onChange={setPin} />
					</>
				)}

				{error && (
					<p
						role="alert"
						className="mt-4 rounded-2xl bg-rose-100 px-4 py-3 font-semibold text-rose-900"
					>
						{error}
					</p>
				)}

				<div className="mt-6 flex flex-wrap gap-3">
					<Button size="lg" onClick={() => void submit()} disabled={busy}>
						{busy ? "One moment…" : mode === "create" ? "Make my card" : "Sign in"}
					</Button>
					<Button size="lg" tone="ghost" onClick={onCancel}>
						Not now
					</Button>
				</div>

				<p className="mt-6 text-sm text-indigo-900/60 dark:text-indigo-100/60">
					We never ask for your name, your email or your age. A player card is just a made-up name,
					an animal and a PIN.
				</p>
			</Card>
		</Panel>
	);
}

function PinField({
	label,
	value,
	onChange,
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
}) {
	return (
		<label className="mb-4 block">
			<span className="mb-2 block font-semibold">{label}</span>
			<input
				value={value}
				onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 4))}
				inputMode="numeric"
				autoComplete="off"
				type="password"
				placeholder="••••"
				className="w-full rounded-2xl bg-white/85 px-4 py-3 text-center text-3xl font-black tracking-[0.5em] shadow-inner dark:bg-black/25"
			/>
		</label>
	);
}
